import React, { useMemo } from 'react'

// ── Constants ─────────────────────────────────────────────────────────────────

const DEPT_HEX = {
  MX: '#22c55e', AVI: '#3b82f6', INT: '#f97316',
  PA: '#ec4899', QA: '#eab308',
}
const DEPT_LABELS = {
  MX: 'Maintenance', AVI: 'Avionics', INT: 'Interior',
  PA: 'Paint', QA: 'Quality Assurance',
}

// ── Utilities (exported for CustomerUpdatePanel) ──────────────────────────────

export function calcPct(subtaskIds, allSubtasks) {
  const sel = allSubtasks.filter(s => subtaskIds.includes(s.id) && s.start_date && s.end_date)
  if (!sel.length) return null
  const earliest = sel.reduce((m, s) => s.start_date < m ? s.start_date : m, sel[0].start_date)
  const latest   = sel.reduce((m, s) => s.end_date   > m ? s.end_date   : m, sel[0].end_date)
  const start = new Date(earliest + 'T00:00:00')
  const end   = new Date(latest   + 'T00:00:00')
  end.setDate(end.getDate() + 1) // end date is inclusive: 100% only after the day has passed
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const total = (end - start) / 86400000
  if (total <= 0) return today >= end ? 100 : 0
  return Math.round(Math.min(Math.max((today - start) / 86400000, 0), total) / total * 100)
}

export function calcLatestEndDate(subtaskIds, allSubtasks) {
  const sel = allSubtasks.filter(s => subtaskIds?.includes(s.id) && s.end_date)
  if (!sel.length) return null
  return sel.reduce((m, s) => s.end_date > m ? s.end_date : m, sel[0].end_date)
}

export function getMilestoneStartDate(m, allSubtasks) {
  if (m.milestone_date) return m.milestone_date
  const sel = allSubtasks.filter(s => (m.subtask_ids || []).includes(s.id) && s.start_date)
  if (!sel.length) return null
  return sel.reduce((min, s) => s.start_date < min ? s.start_date : min, sel[0].start_date)
}

export function getMilestonePct(m, allSubtasks) {
  if (m.milestone_date) {
    const d     = new Date(m.milestone_date + 'T00:00:00')
    const today = new Date(); today.setHours(0, 0, 0, 0)
    return d <= today ? 100 : 0
  }
  if (m.pct_override !== null && m.pct_override !== undefined) return m.pct_override
  return calcPct(m.subtask_ids || [], allSubtasks) ?? 0
}

function getMilestoneDate(m, allSubtasks) {
  if (m.milestone_date) return m.milestone_date
  return calcLatestEndDate(m.subtask_ids || [], allSubtasks)
}

function fmtDate(iso) {
  if (!iso) return ''
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function fmtDateShort(iso) {
  if (!iso) return ''
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function getDeptColor(dept, orgDeptMap) {
  return orgDeptMap[dept]?.hex || DEPT_HEX[dept] || '#6b7280'
}

function getDeptLabel(dept, orgDeptMap) {
  return orgDeptMap[dept]?.label || DEPT_LABELS[dept] || dept
}

// ── Pipeline (exported — also used in editor mode) ────────────────────────────

export function Pipeline({ deptRows, orgDeptMap, allSubtasks, arrivalDate, departureDate }) {
  const milestoneNodes = useMemo(() => {
    const all = []
    deptRows.forEach((row, deptIdx) => {
      row.milestones.forEach((m, mIdx) => {
        all.push({ ...m, dept: row.dept, deptIdx, mIdx, effectiveDate: getMilestoneDate(m, allSubtasks) })
      })
    })
    // Sort: dated milestones first (chronologically), then undated in dept/milestone order
    all.sort((a, b) => {
      if (a.effectiveDate && b.effectiveDate) return a.effectiveDate.localeCompare(b.effectiveDate)
      if (a.effectiveDate) return -1
      if (b.effectiveDate) return 1
      if (a.deptIdx !== b.deptIdx) return a.deptIdx - b.deptIdx
      return a.mIdx - b.mIdx
    })
    const withPct = all.map(n => ({ ...n, pct: getMilestonePct(n, allSubtasks) }))
    const anyInProgress = withPct.some(n => n.pct > 0 && n.pct < 100)
    let promotedNext = false
    return withPct.map(n => {
      let state
      if (n.pct >= 100) {
        state = 'complete'
      } else if (n.pct > 0) {
        state = 'active'
      } else if (!anyInProgress && !promotedNext) {
        state = 'active'
        promotedNext = true
      } else {
        state = 'upcoming'
      }
      return { ...n, state }
    })
  }, [deptRows, allSubtasks])

  const allItems = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const items = []
    if (arrivalDate) {
      const d = new Date(arrivalDate + 'T00:00:00')
      items.push({ type: 'arrival', state: d <= today ? 'complete' : 'upcoming', date: arrivalDate })
    }
    milestoneNodes.forEach(n => items.push({ type: 'milestone', ...n }))
    if (departureDate) {
      const d = new Date(departureDate + 'T00:00:00')
      items.push({ type: 'delivery', state: d <= today ? 'complete' : 'target', date: departureDate })
    }
    return items
  }, [arrivalDate, departureDate, milestoneNodes])

  if (!allItems.length) return null

  return (
    <div className="overflow-x-auto">
      <div className="flex items-start" style={{ minWidth: 'max-content', gap: 0 }}>
        {allItems.map((item, i) => {
          const isLast = i === allItems.length - 1

          if (item.type === 'arrival' || item.type === 'delivery') {
            const isArrival = item.type === 'arrival'
            const isComplete = item.state === 'complete'
            const connectorColor = isComplete ? '#d1d5db' : '#e5e7eb'
            return (
              <div key={item.type} className="flex items-start">
                <div className="flex flex-col items-center" style={{ width: 120 }}>
                  {/* Circle */}
                  <div
                    className="flex items-center justify-center w-12 h-12 rounded-full border-2 mb-2 flex-shrink-0"
                    style={{
                      borderColor: isComplete ? '#d1d5db' : isArrival ? '#9ca3af' : 'rgb(var(--brand-primary-rgb))',
                      backgroundColor: isComplete ? '#f9fafb' : isArrival ? '#f9fafb' : 'rgba(var(--brand-primary-rgb), 0.06)',
                    }}
                  >
                    {isComplete ? (
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : isArrival ? (
                      <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24" style={{ color: 'rgb(var(--brand-primary-rgb))' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.125A59.769 59.769 0 0121.485 12 59.768 59.768 0 013.27 20.875L5.999 12zm0 0h7.5" />
                      </svg>
                    )}
                  </div>
                  {/* Label */}
                  <div className="text-center px-1" style={{ width: 120 }}>
                    <div
                      className="text-[11px] font-bold uppercase tracking-widest"
                      style={{ color: isComplete ? '#9ca3af' : '#374151' }}
                    >
                      {isArrival ? 'Arrival' : 'Delivery'}
                    </div>
                    <div className="text-[13px] font-semibold mt-0.5" style={{ color: isComplete ? '#9ca3af' : '#111827' }}>
                      {fmtDateShort(item.date)}
                    </div>
                  </div>
                </div>
                {!isLast && (
                  <div className="flex-shrink-0 mt-[23px]" style={{ width: 28 }}>
                    <div style={{ height: 2, width: '100%', backgroundColor: connectorColor }} />
                  </div>
                )}
              </div>
            )
          }

          // Milestone node
          const node = item
          const color = getDeptColor(node.dept, orgDeptMap)
          const isPctBased = !node.milestone_date && !node.show_subtask_date
          return (
            <div key={node.id} className="flex items-start">
              <div className="flex flex-col items-center" style={{ width: 120 }}>
                {/* Circle */}
                <div
                  className="flex items-center justify-center w-12 h-12 rounded-full border-2 relative mb-2 flex-shrink-0"
                  style={{
                    borderColor:     node.state === 'active' ? 'rgb(var(--brand-primary-rgb))' : node.state === 'complete' ? '#d1d5db' : '#e5e7eb',
                    backgroundColor: node.state === 'active' ? 'rgb(var(--brand-primary-rgb))' : node.state === 'complete' ? '#f9fafb' : '#fff',
                    boxShadow:       node.state === 'active' ? '0 0 0 5px rgba(var(--brand-primary-rgb), 0.12)' : 'none',
                  }}
                >
                  {node.state === 'complete' ? (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : node.state === 'active' ? (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <polygon points="7,4 20,12 7,20" />
                    </svg>
                  ) : (
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                  )}
                  {/* Dept dot */}
                  <div
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white"
                    style={{ backgroundColor: color }}
                  />
                </div>
                {/* Label */}
                <div className="text-center px-1" style={{ width: 120 }}>
                  <div
                    className="text-[13px] font-semibold leading-tight break-words"
                    style={{ color: node.state === 'complete' ? '#9ca3af' : node.state === 'active' ? '#111827' : '#6b7280' }}
                  >
                    {node.name || '—'}
                  </div>
                  {node.state === 'active' && isPctBased && (
                    <div className="text-[18px] font-black leading-none mt-1" style={{ color: 'rgb(var(--brand-primary-rgb))' }}>
                      {node.pct}%
                    </div>
                  )}
                  {node.effectiveDate && (
                    <div
                      className="text-[13px] font-semibold mt-0.5"
                      style={{ color: node.state === 'complete' ? '#9ca3af' : node.state === 'active' ? '#374151' : '#9ca3af' }}
                    >
                      {fmtDateShort(node.effectiveDate)}
                    </div>
                  )}
                </div>
              </div>
              {/* Connector */}
              {!isLast && (
                <div className="flex-shrink-0 mt-[23px]" style={{ width: 28 }}>
                  <div style={{ height: 2, width: '100%', backgroundColor: node.state === 'complete' ? '#d1d5db' : '#e5e7eb' }} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Dept Section ──────────────────────────────────────────────────────────────

function DeptSection({ deptRow, orgDeptMap, allSubtasks }) {
  const { dept, milestones } = deptRow
  const color = getDeptColor(dept, orgDeptMap)
  const label = getDeptLabel(dept, orgDeptMap)
  const deptPct = milestones.length
    ? Math.round(milestones.reduce((s, m) => s + getMilestonePct(m, allSubtasks), 0) / milestones.length)
    : 0

  return (
    <div className="mb-3 last:mb-0">
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-2.5 bg-gray-50/80 rounded-t-xl border border-gray-200 border-b-0"
        style={{ borderLeftWidth: 3, borderLeftColor: color }}
      >
        <span className="text-xs font-bold tracking-widest uppercase text-gray-700">{label}</span>
        <div className="flex-1" />
        <span className="text-xs font-semibold text-gray-500 tabular-nums">{deptPct}%</span>
        <div className="w-20 h-1.5 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
          <div style={{ width: `${deptPct}%`, height: '100%', backgroundColor: color, borderRadius: 9999, transition: 'width 0.4s ease' }} />
        </div>
      </div>
      {/* Milestone rows */}
      <div className="border border-gray-200 rounded-b-xl overflow-hidden">
        {milestones.length === 0 ? (
          <div className="px-4 py-3 text-xs text-gray-400 italic">No milestones configured.</div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200">
              <div className="flex-shrink-0 w-5" />
              <span className="flex-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">Task</span>
              <span className="flex-shrink-0 w-32 text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">% Complete</span>
              <span className="flex-shrink-0 w-20 text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Start Date</span>
              <span className="flex-shrink-0 w-20 text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">End Date</span>
            </div>
          <div className="divide-y divide-gray-100">
            {milestones.map(m => {
              const pct        = getMilestonePct(m, allSubtasks)
              const isComplete = pct >= 100
              const isActive   = pct > 0 && pct < 100
              const startDate  = getMilestoneStartDate(m, allSubtasks)
              const endDate    = getMilestoneDate(m, allSubtasks)
              return (
                <div key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                  {/* Status icon */}
                  <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                    {isComplete ? (
                      <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    ) : isActive ? (
                      <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                        <svg className="w-2.5 h-2.5" fill={color} viewBox="0 0 24 24">
                          <polygon points="7,4 20,12 7,20" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-200" />
                    )}
                  </div>
                  {/* Name */}
                  <span
                    className="flex-1 text-sm truncate"
                    style={{ color: isComplete ? '#9ca3af' : '#111827', fontWeight: isComplete ? 400 : 500 }}
                  >
                    {m.name || '—'}
                  </span>
                  {/* % Complete — bar + number combined */}
                  <div className="flex items-center gap-1.5 flex-shrink-0 w-32 justify-end">
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-gray-100">
                      <div style={{ width: `${pct}%`, height: '100%', backgroundColor: isComplete ? '#d1d5db' : color, borderRadius: 9999, transition: 'width 0.4s ease' }} />
                    </div>
                    <span className="text-xs tabular-nums flex-shrink-0 w-9 text-right font-semibold" style={{ color: isComplete ? '#9ca3af' : '#6b7280' }}>
                      {pct}%
                    </span>
                  </div>
                  {/* Start Date */}
                  <span className="text-xs text-gray-400 flex-shrink-0 w-20 text-right">
                    {startDate ? fmtDateShort(startDate) : '—'}
                  </span>
                  {/* End Date */}
                  <span className="text-xs text-gray-400 flex-shrink-0 w-20 text-right">
                    {endDate ? fmtDateShort(endDate) : '—'}
                  </span>
                </div>
              )
            })}
          </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── CustomerUpdateDisplay ─────────────────────────────────────────────────────

export default function CustomerUpdateDisplay({ data, subtasks = [], orgDeptMap = {}, orgLogoUrl = '/logo.jpg', project }) {
  const overallPct = useMemo(() => {
    if (data.overall_pct_override !== null && data.overall_pct_override !== undefined) return data.overall_pct_override
    if (!project?.arrival_date || !project?.departure_date) return 0
    const start = new Date(project.arrival_date + 'T00:00:00')
    const end   = new Date(project.departure_date + 'T00:00:00')
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const total = (end - start) / 86400000
    if (total <= 0) return today >= end ? 100 : 0
    return Math.round(Math.min(Math.max((today - start) / 86400000, 0), total) / total * 100)
  }, [data.overall_pct_override, project?.arrival_date, project?.departure_date])

  const hasDepts = data.dept_rows?.length > 0

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <img
            src={orgLogoUrl} alt="Logo" className="h-9 w-auto"
            onError={e => { e.currentTarget.src = '/logo.jpg' }}
          />
          <div className="w-px h-8 bg-gray-200" />
          <div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.4rem', letterSpacing: '0.06em', color: '#101820', lineHeight: 1 }}>
              {data.header?.registration ? `${data.header.registration} Customer Update` : 'Customer Update'}
            </div>
            <div className="flex items-center gap-3 mt-1">
              {data.header?.work_order && (
                <>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Work Order:</span>
                    <span className="text-xs text-gray-900 font-medium">{data.header.work_order}</span>
                  </div>
                  <span className="text-gray-200 text-xs">|</span>
                </>
              )}
              {data.header?.report_date && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">As Of:</span>
                  <span className="text-xs text-gray-900 font-medium">{fmtDate(data.header.report_date)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress + Pipeline */}
      {hasDepts && (
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Project Progress</span>
            <span className="text-sm font-bold" style={{ color: 'rgb(var(--brand-primary-rgb))' }}>
              {overallPct}% Complete
            </span>
          </div>
          <div className="w-full rounded-full overflow-hidden mb-5" style={{ height: 5, backgroundColor: '#f1f5f9' }}>
            <div style={{ height: '100%', width: `${overallPct}%`, backgroundColor: 'rgb(var(--brand-primary-rgb))', borderRadius: 9999, transition: 'width 0.4s ease' }} />
          </div>
          <Pipeline deptRows={data.dept_rows} orgDeptMap={orgDeptMap} allSubtasks={subtasks} arrivalDate={project?.arrival_date} departureDate={project?.departure_date} />
        </div>
      )}

      {/* Department Breakdown */}
      {hasDepts && (
        <div className="px-6 py-5 border-b border-gray-100">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-4">Department Breakdown</span>
          {data.dept_rows.map(row => (
            <DeptSection key={row.id} deptRow={row} orgDeptMap={orgDeptMap} allSubtasks={subtasks} />
          ))}
        </div>
      )}

    </div>
  )
}
